import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { track } from '../components/landing/shared';
import { getScanContext, clearScanContext } from '../utils/scanSession';

const API = process.env.REACT_APP_BACKEND_URL;

const LIME = '#c8f135';
const DISP = "'Syne', system-ui, sans-serif";
const BODY = "'Space Grotesk', system-ui, sans-serif";
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const card = { padding: 22, background: '#101010', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 18 };
const kicker = { fontFamily: MONO, fontSize: 10, letterSpacing: '0.22em', color: LIME, textTransform: 'uppercase', marginBottom: 12 };

const Ring = ({ value }) => {
  const color = value >= 70 ? LIME : value >= 45 ? '#f6c453' : '#ff9a76';
  const c = 2 * Math.PI * 16;
  return (
    <div style={{ position: 'relative', width: 110, height: 110, flexShrink: 0 }}>
      <svg viewBox="0 0 36 36" width={110} height={110} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2.5" />
        <circle cx="18" cy="18" r="16" fill="none" stroke={color} strokeWidth="2.5" strokeDasharray={c} strokeDashoffset={c * (1 - (value || 0) / 100)} strokeLinecap="round" />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontFamily: DISP, fontSize: 32, color, fontWeight: 700 }}>{value || 0}</span>
        <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '0.18em', color: 'rgba(245,240,232,0.5)', textTransform: 'uppercase' }}>health</span>
      </div>
    </div>
  );
};

export default function StrategyReadyPage() {
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | ready | expired | none
  const [data, setData] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const load = async () => {
      const scan = getScanContext();
      try {
        if (scan) {
          const resp = await axios.post(`${API}/api/launch-kit/claim-scan`, { scan_token: scan.token });
          setData(resp.data);
          setState('ready');
          track('strategy_unlocked', { host: resp.data.host });
          clearScanContext();
          try { sessionStorage.setItem('scl_launch_kit_url', resp.data.url || scan.url); } catch (e) { /* noop */ }
          return;
        }
        const mine = await axios.get(`${API}/api/launch-kit/my-strategy`);
        if (mine.data?.ok) {
          setData(mine.data);
          setState('ready');
          return;
        }
        setState('none');
      } catch (err) {
        const status = err.response?.status;
        clearScanContext();
        if (status === 404 || status === 409 || status === 410) {
          const mine = await axios.get(`${API}/api/launch-kit/my-strategy`).catch(() => null);
          if (mine?.data?.ok) { setData(mine.data); setState('ready'); return; }
          setState('expired');
        } else {
          setState('expired');
        }
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (state === 'none') navigate('/dashboard', { replace: true });
  }, [state, navigate]);

  const p = data?.preview || {};

  return (
    <div data-testid="strategy-ready-page" style={{ minHeight: '100vh', background: '#05070a', color: '#f2f5ea', fontFamily: BODY, padding: 'clamp(24px, 5vw, 64px) clamp(16px, 4vw, 48px)' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>

        {state === 'loading' && (
          <div data-testid="strategy-ready-loading" style={{ textAlign: 'center', paddingTop: '20vh' }}>
            <p style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.22em', color: LIME, textTransform: 'uppercase' }}>Unlocking your strategy…</p>
            <p style={{ marginTop: 12, fontSize: 14, color: 'rgba(245,240,232,0.5)' }}>Connecting your scan to your new account</p>
          </div>
        )}

        {state === 'expired' && (
          <div data-testid="strategy-ready-expired" style={{ textAlign: 'center', paddingTop: '18vh', maxWidth: 480, margin: '0 auto' }}>
            <h1 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 30, letterSpacing: '-0.02em' }}>That scan session has expired.</h1>
            <p style={{ marginTop: 14, fontSize: 15, lineHeight: 1.7, color: 'rgba(245,240,232,0.65)' }}>
              No problem. Your account is ready, and a fresh scan from your dashboard rebuilds your strategy in under a minute.
            </p>
            <div style={{ marginTop: 28, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button data-testid="strategy-expired-dashboard" onClick={() => navigate('/dashboard')}
                style={{ background: LIME, color: '#05070a', fontFamily: BODY, fontWeight: 700, fontSize: 15, padding: '14px 26px', borderRadius: 12, border: 'none', cursor: 'pointer' }}>
                Go to my dashboard →
              </button>
            </div>
          </div>
        )}

        {state === 'ready' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderRadius: 999, border: '1px solid rgba(200,241,53,0.25)', background: 'rgba(200,241,53,0.05)', marginBottom: 20 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: LIME }} />
                <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.18em', color: LIME, textTransform: 'uppercase' }}>Scan claimed &middot; {data?.host}</span>
              </div>
              <h1 data-testid="strategy-ready-title" style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(30px, 5vw, 52px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}>
                Your strategy is ready.
              </h1>
              <p style={{ marginTop: 14, fontSize: 15.5, color: 'rgba(245,240,232,0.65)', maxWidth: 520, margin: '14px auto 0', lineHeight: 1.7 }}>
                Built from what ScalitOS learned about <strong style={{ color: '#f2f5ea' }}>{data?.brand || data?.host}</strong>. Here is your full, unlocked strategy.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: 18 }}>
              <div style={{ ...card, gridColumn: '1 / -1' }}>
                <p style={kicker}>Brand snapshot</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
                  <Ring value={p.brand_health_score} />
                  <p style={{ fontFamily: DISP, fontSize: 20, lineHeight: 1.4, flex: '1 1 280px', margin: 0 }}>{p.brand_snapshot}</p>
                </div>
              </div>

              <div style={{ ...card, borderLeft: '2px solid #ff9a76' }}>
                <p style={{ ...kicker, color: '#ff9a76' }}>Biggest growth gap</p>
                <p style={{ fontFamily: DISP, fontSize: 18, lineHeight: 1.45, margin: 0 }}>{p.growth_gap}</p>
              </div>

              <div style={card}>
                <p style={kicker}>Industry benchmark</p>
                <p style={{ fontSize: 13.5, lineHeight: 1.7, color: 'rgba(245,240,232,0.78)', margin: 0 }}>{p.industry_benchmark}</p>
              </div>

              <div style={{ ...card, borderLeft: `2px solid ${LIME}`, gridColumn: '1 / -1' }}>
                <p style={kicker}>Recommended campaign</p>
                <h2 style={{ fontFamily: DISP, fontSize: 'clamp(24px, 4vw, 34px)', lineHeight: 1.15, margin: '4px 0 12px', fontWeight: 700, letterSpacing: '-0.01em' }}>{p.campaign_name}.</h2>
                <p style={{ fontSize: 15.5, lineHeight: 1.65, color: 'rgba(245,240,232,0.85)', margin: '0 0 16px' }}>{p.campaign_concept}</p>
                {p.why_this_campaign && (
                  <div style={{ padding: '12px 16px', background: 'rgba(200,241,53,0.03)', borderRadius: 10, borderLeft: '1px solid rgba(200,241,53,0.2)' }}>
                    <p style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.2em', color: 'rgba(245,240,232,0.5)', textTransform: 'uppercase', marginBottom: 6 }}>Why this campaign</p>
                    <p style={{ fontSize: 13, lineHeight: 1.7, color: 'rgba(245,240,232,0.78)', margin: 0 }}>{p.why_this_campaign}</p>
                  </div>
                )}
              </div>

              <div style={{ ...card, gridColumn: '1 / -1' }} data-testid="strategy-ready-week-plan">
                <p style={kicker}>This week&rsquo;s plan &middot; fully unlocked</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
                  {(p.week_plan_preview || []).map((d, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 14px', background: 'rgba(200,241,53,0.04)', border: '1px solid rgba(200,241,53,0.12)', borderRadius: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: MONO, fontSize: 10, color: 'rgba(245,240,232,0.5)', width: 64 }}>{d.day}</span>
                      <span style={{ fontFamily: DISP, fontSize: 14.5, fontWeight: 600, flex: 1, minWidth: 160 }}>{d.title}</span>
                      <span style={{ fontFamily: MONO, fontSize: 9, color: 'rgba(245,240,232,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{d.type}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 36, textAlign: 'center' }}>
              <button
                data-testid="strategy-ready-cta"
                onClick={() => { track('strategy_ready_dashboard_clicked'); navigate('/dashboard'); }}
                style={{ background: LIME, color: '#05070a', fontFamily: BODY, fontWeight: 700, fontSize: 16, padding: '16px 32px', borderRadius: 12, border: 'none', cursor: 'pointer', boxShadow: '0 0 36px rgba(200,241,53,0.25)' }}
              >
                Start executing this plan →
              </button>
              <p style={{ marginTop: 14, fontSize: 12.5, color: 'rgba(245,240,232,0.45)' }}>
                Your brand profile is pre-filled from the scan, so there is no need to enter your website again.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
